/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/activity/ActivityFeedTypePills.tsx
 * PURPOSE: Compact type filter row for ActivityFeed (All, Calls, Emails, Meetings, Notes)
 * DEPENDENCIES: react, tailwind-merge
 * PUBLIC_API: ActivityFeedTypeFilter, ActivityFeedTypePills
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 52/150
 */

import { twMerge } from 'tailwind-merge';
import type { ActivityType } from '../../types/activity.types';

export type ActivityFeedTypeFilter = 'all' | Exclude<ActivityType, 'task'>;

const PILLS: { id: ActivityFeedTypeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'call', label: 'Calls' },
  { id: 'email', label: 'Emails' },
  { id: 'meeting', label: 'Meetings' },
  { id: 'note', label: 'Notes' },
];

export type ActivityFeedTypePillsProps = {
  value: ActivityFeedTypeFilter;
  onChange: (next: ActivityFeedTypeFilter) => void;
};

export function ActivityFeedTypePills({ value, onChange }: ActivityFeedTypePillsProps) {
  return (
    <div
      className="mb-4 flex flex-wrap gap-1.5"
      role="tablist"
      aria-label="Filter activity by type"
      data-testid="activity-feed-type-filters"
    >
      {PILLS.map((p) => {
        const active = value === p.id;
        return (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`activity-feed-filter-${p.id}`}
            onClick={() => onChange(p.id)}
            className={twMerge(
              'min-h-[44px] rounded-full px-3 text-xs font-medium transition-colors',
              active
                ? 'bg-primary-600 text-white shadow-sm dark:bg-primary-500'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
            )}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}
